import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo
} from "react";
import dayjs, { Dayjs } from "dayjs";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker
} from "@material-ui/pickers";
import DayjsUtils from "@date-io/dayjs";
import { Popover, Button, Box } from "@material-ui/core";
import { useI18n } from "../../../hooks/useI18n";
import "dayjs/locale/sv";
type DateRange = [Dayjs, Dayjs];

interface Shortcut {
  label: string;
  getValue: () => DateRange;
}

interface DateRangePickerProps {
  onDateRangeChange: (range: DateRange) => void;
  shortcutsToExclude?: string[];
  defaultSelectedShortcut?: string;
  maxDate?: Dayjs;
}

const defaultShortcuts = (t: (key: string) => string): Shortcut[] => [
  {
    label: t("common.today"),
    getValue: () => {
      const today = dayjs();
      return [today.startOf("day"), today.endOf("day")] as DateRange;
    }
  },
  {
    label: t("common.yesterday"),
    getValue: () => {
      const yesterday = dayjs().subtract(1, "day");
      return [yesterday.startOf("day"), yesterday.endOf("day")] as DateRange;
    }
  },
  {
    label: t("common.thisWeek"),
    getValue: () => {
      const now = dayjs();
      const startOfWeek = now.startOf("week");
      const endOfWeek = now.endOf("week");
      const maxAllowedDate = now.subtract(1, "day").endOf("day");

      return [
        startOfWeek,
        endOfWeek.isAfter(maxAllowedDate) ? maxAllowedDate : endOfWeek
      ] as DateRange;
    }
  },
  {
    label: t("common.thisMonth"),
    getValue: () => {
      const now = dayjs();
      const startOfMonth = now.startOf("month");
      const endOfMonth = now.endOf("month");
      const maxAllowedDate = now.subtract(1, "day").endOf("day");

      return [
        startOfMonth,
        endOfMonth.isAfter(maxAllowedDate) ? maxAllowedDate : endOfMonth
      ] as DateRange;
    }
  },
  {
    label: t("common.reset"),
    getValue: () => {
      const today = dayjs();
      return [today.startOf("day"), today.endOf("day")] as DateRange;
    }
  }
];

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  onDateRangeChange,
  shortcutsToExclude = [],
  defaultSelectedShortcut,
  maxDate
}) => {
  const { t } = useI18n();
  const filteredShortcuts = useMemo(
    () =>
      defaultShortcuts(t).filter(
        shortcut => !shortcutsToExclude.includes(shortcut.label)
      ),
    [shortcutsToExclude, t]
  );

  const [startDate, setStartDate] = useState<Dayjs>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs>(dayjs());
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const hasAppliedDefaultShortcut = useRef(false);

  useEffect(() => {
    if (defaultSelectedShortcut && !hasAppliedDefaultShortcut.current) {
      const shortcut = filteredShortcuts.find(
        s => s.label === defaultSelectedShortcut
      );
      if (shortcut) {
        const [start, end] = shortcut.getValue();
        setStartDate(start);
        setEndDate(end);
        onDateRangeChange([start, end]);
        hasAppliedDefaultShortcut.current = true;
      }
    }
  }, [defaultSelectedShortcut, filteredShortcuts, onDateRangeChange]);

  const handleOpen = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    []
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const applyShortcut = useCallback(
    (shortcut: () => DateRange) => {
      const [start, end] = shortcut();
      setStartDate(start);
      setEndDate(end);
      onDateRangeChange([start, end]);
      handleClose();
    },
    [onDateRangeChange, handleClose]
  );

  const applySelection = useCallback(() => {
    if (startDate && endDate) {
      onDateRangeChange([startDate, endDate]);
      handleClose();
    }
  }, [startDate, endDate, onDateRangeChange, handleClose]);

  const handleStartDateChange = useCallback(
    (date: Dayjs | null) => {
      if (date && date.isValid()) {
        const maxAllowedDate =
          maxDate || dayjs().subtract(1, "day").endOf("day");
        const selectedDate = date.isAfter(maxAllowedDate)
          ? maxAllowedDate
          : date.startOf("day");

        setStartDate(selectedDate);
        if (endDate && selectedDate.isAfter(endDate)) {
          setEndDate(selectedDate.endOf("day"));
        }
      }
    },
    [endDate, maxDate]
  );

  const handleEndDateChange = useCallback(
    (date: Dayjs | null) => {
      if (date && date.isValid()) {
        const maxAllowedDate =
          maxDate || dayjs().subtract(1, "day").endOf("day");
        const selectedDate = date.isAfter(maxAllowedDate)
          ? maxAllowedDate
          : date.endOf("day");

        setEndDate(selectedDate);
        if (startDate && selectedDate.isBefore(startDate)) {
          setStartDate(selectedDate.startOf("day"));
        }
      }
    },
    [startDate, maxDate]
  );

  const formattedRange = useMemo(() => {
    if (startDate && endDate) {
      const matchingShortcut = filteredShortcuts.find(shortcut => {
        const [shortcutStart, shortcutEnd] = shortcut.getValue();
        return (
          startDate.isSame(shortcutStart, "day") &&
          endDate.isSame(shortcutEnd, "day")
        );
      });

      if (matchingShortcut) {
        return matchingShortcut.label;
      }

      return `${startDate.format("YYYY-MM-DD")} - ${endDate.format(
        "YYYY-MM-DD"
      )}`;
    }

    return "Select a date range";
  }, [startDate, endDate, filteredShortcuts]);

  return (
    <MuiPickersUtilsProvider utils={DayjsUtils}>
      <div>
        <Button
          variant="outlined"
          onClick={handleOpen}
          style={{
            width: "300px",
            textAlign: "left",
            justifyContent: "space-between",
            display: "flex"
          }}
          aria-haspopup="true"
          aria-expanded={Boolean(anchorEl)}
        >
          {formattedRange}
        </Button>

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left"
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left"
          }}
        >
          <Box display="flex" style={{ padding: "16px" }}>
            <Box
              display="flex"
              flexDirection="column"
              style={{
                marginRight: "16px",
                borderRight: "1px solid #ddd",
                paddingRight: "16px"
              }}
            >
              {filteredShortcuts.map((shortcut, index) => (
                <Button
                  key={index}
                  onClick={() => applyShortcut(shortcut.getValue)}
                  style={{
                    marginBottom: "8px",
                    textTransform: "none"
                  }}
                  variant="outlined"
                >
                  {shortcut.label}
                </Button>
              ))}
            </Box>

            <Box display="flex" flexDirection="column">
              <MuiPickersUtilsProvider utils={DayjsUtils} locale="sv">
                <KeyboardDatePicker
                  label={t("common.startDate")}
                  value={startDate}
                  onChange={handleStartDateChange}
                  format="YYYY-MM-DD"
                  style={{ marginBottom: "16px" }}
                  maxDate={maxDate || dayjs().subtract(1, "day")}
                  cancelLabel={t("common.cancel")}
                  okLabel={t("common.ok")}
                  disableToolbar
                />
                <KeyboardDatePicker
                  label={t("common.endDate")}
                  value={endDate}
                  onChange={handleEndDateChange}
                  format="YYYY-MM-DD"
                  style={{ marginBottom: "16px" }}
                  maxDate={maxDate || dayjs().subtract(1, "day")}
                  cancelLabel={t("common.cancel")}
                  okLabel={t("common.ok")}
                  disableToolbar
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={applySelection}
                  style={{ marginTop: "8px" }}
                >
                  {t("common.apply")}
                </Button>
              </MuiPickersUtilsProvider>
            </Box>
          </Box>
        </Popover>
      </div>
    </MuiPickersUtilsProvider>
  );
};

export default DateRangePicker;
